/**
 * App-bar quick-access shortcuts for the Mail and Files utility modules.
 *
 * These two modules are reachable only via the module-picker's pinned strip;
 * this restores always-visible entry points next to the notification icons.
 *
 * Which entries appear is driven by {@link useMailFilesShortcuts} (feature
 * enablement + live Google/Microsoft integrations); HOW they render — direct
 * icons vs. grouped dropdowns — is driven by {@link computeShortcutLayout}.
 *
 * Two variants:
 * - `inline` (default): icon buttons / dropdowns for the desktop header cluster.
 * - `menu`: flat list rows for the mobile header overflow menu (always direct,
 *   never collapsed — a vertical menu has the room).
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Mail, Files } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useMailFilesShortcuts } from './appBarShortcuts/useMailFilesShortcuts';
import { computeShortcutLayout, groupUnreadTotal, ShortcutItem } from './appBarShortcuts/computeShortcutLayout';
import type { ReactNode } from 'react';

/** Small unread-count badge, mirroring the AppBar notification badge style. */
function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface AppBarMailFilesShortcutsProps {
  /**
   * A CSS color string for the module accent — pass `MODULE_ACCENT_COLOR`
   * (`shared/utils/color`), never a raw module hex. The raw hex has arbitrary
   * lightness per module and goes unreadable in one of the two themes.
   */
  moduleColor: string;
  variant?: 'inline' | 'menu';
  /** Called after a shortcut is activated (e.g. to close the mobile menu). */
  onNavigate?: () => void;
}

export function AppBarMailFilesShortcuts({
  moduleColor,
  variant = 'inline',
  onNavigate,
}: AppBarMailFilesShortcutsProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { mail, files } = useMailFilesShortcuts();

  const units = useMemo(() => computeShortcutLayout<ReactNode>(mail, files), [mail, files]);

  if (units.length === 0) return null;

  const handleSelect = (item: ShortcutItem<ReactNode>) => {
    navigate(item.path);
    onNavigate?.();
  };

  // Mobile overflow: flat list of every present item, always direct.
  if (variant === 'menu') {
    return (
      <>
        {[...mail, ...files].map((item) => (
          <Button
            key={item.key}
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 font-normal text-foreground"
            onClick={() => handleSelect(item)}
          >
            <span className="[&>svg]:h-4 [&>svg]:w-4 shrink-0">{item.icon}</span>
            <span className="flex-1 text-start">{t(item.labelKey)}</span>
            {(item.unreadCount ?? 0) > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                {(item.unreadCount ?? 0) > 99 ? '99+' : item.unreadCount}
              </span>
            )}
          </Button>
        ))}
      </>
    );
  }

  return (
    <div className="flex items-center">
      {units.map((unit) => {
        if (unit.kind === 'icon') {
          const item = unit.item;
          return (
            <Tooltip key={item.key}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-accent"
                  style={{ color: moduleColor }}
                  aria-label={t(item.labelKey)}
                  onClick={() => handleSelect(item)}
                >
                  {item.icon}
                  <CountBadge count={item.unreadCount ?? 0} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t(item.labelKey)}</TooltipContent>
            </Tooltip>
          );
        }

        const triggerLabel = t(unit.triggerLabelKey);
        const TriggerIcon = unit.group === 'mail' ? Mail : Files;
        const groupUnread = groupUnreadTotal(unit.items);
        return (
          <DropdownMenu key={unit.group}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative hover:bg-accent"
                    style={{ color: moduleColor }}
                    aria-label={triggerLabel}
                  >
                    <TriggerIcon className="h-4 w-4" />
                    <ChevronDown className="absolute bottom-0.5 right-0.5 h-2.5 w-2.5 opacity-70" />
                    <CountBadge count={groupUnread} />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{triggerLabel}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {unit.items.map((item) => (
                <DropdownMenuItem
                  key={item.key}
                  onClick={() => handleSelect(item)}
                  className="gap-2"
                >
                  <span className="[&>svg]:h-4 [&>svg]:w-4 shrink-0">{item.icon}</span>
                  <span className="flex-1">{t(item.labelKey)}</span>
                  {(item.unreadCount ?? 0) > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {(item.unreadCount ?? 0) > 99 ? '99+' : item.unreadCount}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </div>
  );
}
